import { useRollbar } from '@rollbar/react'
import * as pageActions from 'client/actions/pageActions'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { usePage } from 'client/store'
import pageSelectors, {
  isWebsitePageType,
} from 'client/store/page/pageSelectors'
import { BadRequest, PageValidationError } from 'common/errors'
import { AccessDenied } from 'common/errors/AccessDenied'
import { NetworkError } from 'common/errors/NetworkError'
import { NotFound } from 'common/errors/NotFound'
import { useActions } from 'common/hooks/useActions'
import React, { useCallback, useLayoutEffect, useState } from 'react'
import { PlayIcon } from 'src/editor/shared/icons/play-icon'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import { setIsPreviewMode } from 'src/editor/store/website-pages/website-pages-actions'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

export const PreviewButton = () => {
  const { t, i18n } = useTypedTranslation()
  const rollbar = useRollbar()
  const isLocaleInitialised = i18n.language
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useAppDispatch()
  const { isTemplate } = usePageRouteDetector()
  const previewPage = useActions(pageActions.previewPage)
  const pageId = usePage(pageSelectors.getPageId)
  const isWebsitePage = usePage(isWebsitePageType)

  const handlePreview = useCallback(async () => {
    setIsLoading(true)
    try {
      if (isWebsitePage) {
        dispatch(setIsPreviewMode(true))
        setIsLoading(false)
      } else {
        const previewWindow = window.open('', 'preview')
        const { data } = await previewPage(isTemplate)

        if (!previewWindow) {
          return
        }

        setIsLoading(false)
        previewWindow.location.href = data
      }
    } catch (error) {
      if (error instanceof PageValidationError) {
        dispatch(pageActions.pageValidationFail(error.messages))
      } else if (error instanceof NotFound) {
        rollbar.error(error)
      } else if (error instanceof AccessDenied) {
        dispatch(pageActions.previewPageFail('core.errors.login_required'))
      } else if (error instanceof NetworkError) {
        dispatch(pageActions.previewPageFail('core.errors.no_connection'))
      } else if (error instanceof BadRequest) {
        dispatch(pageActions.pageValidationFail(error.response.data))
      } else {
        dispatch(pageActions.previewPageFail('core.errors.preview'))
      }
    }

    setIsLoading(false)
  }, [dispatch, isWebsitePage, previewPage, isTemplate, rollbar])

  useLayoutEffect(() => {
    if (!isWebsitePage) {
      return
    }

    const searchParams = new URLSearchParams(document.location.search)
    const isPreview = Boolean(searchParams.get('preview'))

    if (isPreview) {
      handlePreview()
    }
  }, [handlePreview, isWebsitePage])

  return (
    <GhostIconButton
      onClick={handlePreview}
      title={t('core.header.preview')}
      disabled={!isLocaleInitialised || isLoading || !pageId}
      isLoading={isLoading}
      icon={<PlayIcon title={t('core.header.preview')} />}
    />
  )
}
